import { useMutation } from 'react-query'
import { isProduction } from '../utils/env'
import * as log from '../utils/logging'
import { getDevice } from '../utils/mixpanel'
import useAxios from './useAxios'
import useUser from './useUser'
import { useMixpanel } from 'gatsby-plugin-mixpanel'

export type TelemetryHandler = {
  track: (te: TelemetryEvent) => void
}

export type TelemetryEvent = {
  event: string
  properties?: Record<string, string | number | undefined | null>
}

// Function to extract UTM parameters from the URL
const getUTMParameters = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_term: urlParams.get('utm_term') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,
  }
}

// Backend Telemetry Hook
export function useBETelemetry(): TelemetryHandler {
  const axios = useAxios()
  const { sessionID } = useUser()
  const mixpanel = useMixpanel()

  const mutation = useMutation({
    mutationFn: (te: TelemetryEvent) =>
      axios.post('/api/v2/tracking/event', te, {
        headers: {
          'Content-Type': 'application/json',
        },
      }),
  })

  return {
    track: async (te: TelemetryEvent) => {
      const url = new URL(window.location.href)
      const deviceId = mixpanel?.get_property('$device_id') || 'unknown_device'
      const referrerURL = document.referrer ? new URL(document.referrer) : null

      const utmParameters = getUTMParameters()
      const properties = {
        event: te.event,
        timestamp: Date.now() / 1000,
        user_agent: navigator.userAgent,
        url: window.location.href,
        referer: document.referrer || 'No referrer',
        device_id: deviceId,
        device: getDevice(),
        visitor_id: sessionID,
        site: window.location.hostname,
        utm_source: utmParameters.utm_source,
        utm_medium: utmParameters.utm_medium,
        utm_campaign: utmParameters.utm_campaign,
        utm_term: utmParameters.utm_term,
        utm_content: utmParameters.utm_content,
        ...te?.properties,
      }

      // if (!isProduction) {
      //   log.table(`[telemetry] ${te.event}`, properties)
      //   return
      // }

      // Always send telemetry data, regardless of the environment
      if (navigator.sendBeacon) {
        const data = JSON.stringify({ event: te.event, properties })
        navigator.sendBeacon('/api/v2/tracking/event', data)
      } else {
        mutation.mutate({ event: te.event, properties })
      }
    },
  }
}

// Frontend Telemetry Hook
export function useFETelemetry(): TelemetryHandler {
  const { partnerId, sessionID } = useUser()
  const mixpanel = useMixpanel()

  return {
    track: (te: TelemetryEvent) => {
      const url = new URL(window.location.href)
      const pt = url.searchParams.get('pt')

      const properties = {
        page_path: window.location.pathname,
        website_host: window.location.hostname,
        session_id: sessionID,
        device: getDevice(),
        ...(pt && { partner_key: pt, partner_id: partnerId }),
        ...te?.properties,
      }

      if (!isProduction) {
        log.table(`[telemetry] ${te.event}`, properties)
        return
      }

      mixpanel.track(te.event, properties, { send_immediately: true })
    },
  }
}
